import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Search, Check, Trash2, X, ImagePlus, UserPlus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
    { value: "paquete", label: "Paquete" },
    { value: "correspondencia", label: "Correspondencia" },
    { value: "otro", label: "Otro" },
];

const EMPRESAS = [
    "Mercado Libre", "Correo Argentino", "Andreani", "OCA", "DHL", "FedEx", "UPS", "Otro",
];

function fmtCuando(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const ayer = new Date(today); ayer.setDate(ayer.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay(d, today)) return `Hoy ${hh}:${mm}`;
    if (sameDay(d, ayer)) return `Ayer ${hh}:${mm}`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mo} ${hh}:${mm}`;
}

async function shrinkImage(file, maxW = 1000, quality = 0.7) {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = url;
        });
        const ratio = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", quality);
    } finally {
        URL.revokeObjectURL(url);
    }
}

const emptyForm = {
    // Modo destinatario
    destinatario: null,        // objeto del buscador o null
    manual: false,             // true => carga manual
    unidad_id_manual: "",
    destinatario_nombre_manual: "",
    // Resto
    tipo: "paquete",
    empresa_select: "",        // valor del select
    empresa_otro: "",          // texto si seleccionó "Otro"
    observacion: "",
    foto_url: "",
};

export default function Paqueteria() {
    const [items, setItems] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [tab, setTab] = useState("pendientes");
    const [query, setQuery] = useState("");
    const [unidadFiltro, setUnidadFiltro] = useState("__all__");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [retirarFor, setRetirarFor] = useState(null);

    const load = () => api.get("/paqueteria").then((r) => setItems(r.data));
    useEffect(() => {
        api.get("/unidades").then((r) => setUnidades(r.data));
        load();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((p) => {
            if (tab === "pendientes" && p.estado !== "pendiente") return false;
            if (tab === "historial" && p.estado !== "retirado") return false;
            if (unidadFiltro !== "__all__" && p.unidad_id !== unidadFiltro) return false;
            if (!q) return true;
            return (
                (p.destinatario_nombre || "").toLowerCase().includes(q)
                || (p.empresa_origen || "").toLowerCase().includes(q)
                || (p.unidad_etiqueta || "").toLowerCase().includes(q)
                || (p.observacion || "").toLowerCase().includes(q)
                || (p.retirado_por_nombre || "").toLowerCase().includes(q)
            );
        });
    }, [items, tab, query, unidadFiltro]);

    const pendCount = items.filter((p) => p.estado === "pendiente").length;
    const histCount = items.filter((p) => p.estado === "retirado").length;

    const empresaFinal = () => {
        if (form.empresa_select === "Otro") return form.empresa_otro.trim();
        return form.empresa_select || "";
    };

    const canSubmit = () => {
        if (form.manual) {
            return form.unidad_id_manual && form.destinatario_nombre_manual.trim();
        }
        return !!form.destinatario;
    };

    const submit = async () => {
        if (!canSubmit()) return;
        setBusy(true);
        try {
            const payload = {
                tipo: form.tipo,
                empresa_origen: empresaFinal(),
                observacion: form.observacion,
                foto_url: form.foto_url,
            };
            if (form.manual) {
                payload.unidad_id = form.unidad_id_manual;
                payload.destinatario_nombre = form.destinatario_nombre_manual.trim();
            } else {
                payload.destinatario_usuario_edif_id = form.destinatario.usuario_edif_id;
            }
            await api.post("/paqueteria", payload);
            setForm(emptyForm);
            setOpen(false);
            await load();
            setTab("pendientes");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const onPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const dataUrl = await shrinkImage(f);
            setForm((s) => ({ ...s, foto_url: dataUrl }));
        } catch {
            toast.error("No se pudo procesar la foto");
        }
    };

    const confirmarRetiro = async (data) => {
        if (!retirarFor) return;
        setBusy(true);
        try {
            await api.post(`/paqueteria/${retirarFor.paquete_id}/retirar`, data);
            setRetirarFor(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div>
            <PageHeader title="Correo y paquetería" subtitle="Cargá los paquetes que llegan y marcalos como retirados." />

            {/* Toolbar */}
            <div className="flex flex-col md:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar por unidad, destinatario o empresa…"
                        className="pl-8 h-9"
                        data-testid="paq-search"
                    />
                </div>
                <Select value={unidadFiltro} onValueChange={setUnidadFiltro}>
                    <SelectTrigger className="w-full md:w-44 h-9" data-testid="paq-unidad-filter">
                        <SelectValue placeholder="Unidad" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">Todas las unidades</SelectItem>
                        {unidades.map((u) => (
                            <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.etiqueta}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button onClick={() => { setForm(emptyForm); setOpen(true); }} data-testid="paq-new" className="h-9">
                    <Plus className="h-4 w-4 mr-1" /> Nuevo paquete
                </Button>
            </div>

            {/* Tabs */}
            <div className="inline-flex rounded-md border border-border overflow-hidden h-8 mb-3">
                {[
                    { v: "pendientes", l: `Pendientes (${pendCount})` },
                    { v: "historial", l: `Historial (${histCount})` },
                ].map((t) => (
                    <button
                        key={t.v}
                        type="button"
                        onClick={() => setTab(t.v)}
                        data-testid={`paq-tab-${t.v}`}
                        className={`px-3 text-xs font-medium transition-colors ${tab === t.v ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <EmptyState
                    title={tab === "pendientes" ? "No hay paquetes pendientes" : "Sin historial"}
                    description={query ? "Probá con otra búsqueda." : ""}
                />
            ) : (
                <Card className="overflow-hidden">
                    {/* Desktop table */}
                    <div className="hidden md:block">
                        <table className="w-full text-[13px]">
                            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left font-semibold px-3 py-2 w-[100px]">Llegó</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[90px]">Unidad</th>
                                    <th className="text-left font-semibold px-3 py-2">Destinatario</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[110px]">Tipo</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[140px]">Empresa</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[120px]">Estado</th>
                                    {tab === "historial" && (
                                        <th className="text-left font-semibold px-3 py-2 w-[150px]">Retirado por</th>
                                    )}
                                    <th className="px-3 py-2 w-[120px]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => (
                                    <tr
                                        key={p.paquete_id}
                                        onClick={() => setDetalle(p)}
                                        className={`border-t border-border hover:bg-primary-soft/30 cursor-pointer ${p.estado === "retirado" ? "opacity-70" : ""}`}
                                        data-testid={`paq-row-${p.paquete_id}`}
                                    >
                                        <td className="px-3 py-1.5 font-mono text-primary whitespace-nowrap">{fmtCuando(p.fecha_llegada || p.creado_en)}</td>
                                        <td className="px-3 py-1.5 font-mono text-muted-foreground">{p.unidad_etiqueta}</td>
                                        <td className="px-3 py-1.5 truncate">
                                            {p.destinatario_nombre || <span className="text-muted-foreground italic">—</span>}
                                            {p.destinatario_manual && <span className="ml-1 text-[10px] text-muted-foreground">(manual)</span>}
                                        </td>
                                        <td className="px-3 py-1.5 capitalize text-muted-foreground">{p.tipo}</td>
                                        <td className="px-3 py-1.5 truncate text-muted-foreground">{p.empresa_origen || "—"}</td>
                                        <td className="px-3 py-1.5">
                                            {p.estado === "pendiente" ? (
                                                <Badge className="bg-accent/15 text-accent border border-accent/30 hover:bg-accent/15 text-[10px] font-medium">Pendiente</Badge>
                                            ) : (
                                                <Badge className="bg-success/15 text-success border border-success/40 hover:bg-success/15 text-[10px] font-medium">
                                                    Retirado · {fmtCuando(p.fecha_retiro)}
                                                </Badge>
                                            )}
                                        </td>
                                        {tab === "historial" && (
                                            <td className="px-3 py-1.5 text-muted-foreground truncate">
                                                {p.retirado_por_nombre || "—"}
                                                {p.retirado_por_tipo === "otra_persona" && p.retirado_por_dni && (
                                                    <span className="text-[10px] ml-1">· DNI {p.retirado_por_dni}</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-3 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                                            {p.estado === "pendiente" && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => setRetirarFor(p)}
                                                    disabled={busy}
                                                    className="h-7 px-2.5 text-xs"
                                                    data-testid={`paq-retirar-${p.paquete_id}`}
                                                >
                                                    <Check className="h-3.5 w-3.5 mr-1" /> Retirado
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden divide-y divide-border">
                        {filtered.map((p) => (
                            <div
                                key={p.paquete_id}
                                onClick={() => setDetalle(p)}
                                className={`p-3 ${p.estado === "retirado" ? "opacity-70" : ""}`}
                                data-testid={`paq-row-${p.paquete_id}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm truncate">{p.destinatario_nombre || p.unidad_etiqueta}</p>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {p.unidad_etiqueta} · {p.tipo}{p.empresa_origen ? ` · ${p.empresa_origen}` : ""}
                                        </p>
                                        {p.estado === "retirado" && p.retirado_por_nombre && (
                                            <p className="text-[11px] text-muted-foreground truncate">Retirado por {p.retirado_por_nombre}</p>
                                        )}
                                    </div>
                                    <div className="font-mono text-xs text-primary shrink-0">{fmtCuando(p.fecha_llegada || p.creado_en)}</div>
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2">
                                    {p.estado === "pendiente" ? (
                                        <Badge className="bg-accent/15 text-accent border border-accent/30 text-[10px]">Pendiente</Badge>
                                    ) : (
                                        <Badge className="bg-success/15 text-success border border-success/40 text-[10px]">
                                            Retirado · {fmtCuando(p.fecha_retiro)}
                                        </Badge>
                                    )}
                                    {p.estado === "pendiente" && (
                                        <Button
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); setRetirarFor(p); }}
                                            disabled={busy}
                                            className="h-7 px-2.5 text-xs"
                                            data-testid={`paq-retirar-${p.paquete_id}`}
                                        >
                                            <Check className="h-3.5 w-3.5 mr-1" /> Retirado
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Modal nuevo paquete */}
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Nuevo paquete</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        {/* Destinatario */}
                        {!form.manual ? (
                            <DestinatarioPicker
                                selected={form.destinatario}
                                onSelect={(d) => setForm((s) => ({ ...s, destinatario: d }))}
                                onManual={() => setForm((s) => ({ ...s, manual: true, destinatario: null }))}
                            />
                        ) : (
                            <ManualDestinatario
                                form={form}
                                unidades={unidades}
                                onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
                                onBack={() => setForm((s) => ({ ...s, manual: false, unidad_id_manual: "", destinatario_nombre_manual: "" }))}
                            />
                        )}

                        <div>
                            <Label className="text-xs">Tipo</Label>
                            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                <SelectTrigger data-testid="paq-form-tipo"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs">Empresa / origen</Label>
                            <Select
                                value={form.empresa_select}
                                onValueChange={(v) => setForm({ ...form, empresa_select: v, empresa_otro: v === "Otro" ? form.empresa_otro : "" })}
                            >
                                <SelectTrigger data-testid="paq-form-empresa"><SelectValue placeholder="Elegí una opción" /></SelectTrigger>
                                <SelectContent>
                                    {EMPRESAS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {form.empresa_select === "Otro" && (
                                <Input
                                    value={form.empresa_otro}
                                    onChange={(e) => setForm({ ...form, empresa_otro: e.target.value })}
                                    placeholder="Indicar empresa/origen"
                                    className="mt-2"
                                    data-testid="paq-form-empresa-otro"
                                />
                            )}
                        </div>

                        <div>
                            <Label className="text-xs">Observación (opcional)</Label>
                            <Input
                                value={form.observacion}
                                onChange={(e) => setForm({ ...form, observacion: e.target.value })}
                                placeholder="Notas internas"
                                data-testid="paq-form-obs"
                            />
                        </div>

                        <div>
                            <Label className="text-xs">Foto (opcional)</Label>
                            {form.foto_url ? (
                                <div className="relative inline-block">
                                    <img src={form.foto_url} alt="paquete" className="rounded-md border border-border max-h-40" />
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, foto_url: "" })}
                                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ) : (
                                <label className="flex items-center gap-2 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/40">
                                    <ImagePlus className="h-4 w-4" />
                                    <span>Adjuntar foto</span>
                                    <input type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="paq-form-foto" />
                                </label>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={busy || !canSubmit()} data-testid="paq-form-submit">Registrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal marcar retirado */}
            <RetirarModal
                paquete={retirarFor}
                busy={busy}
                onClose={() => setRetirarFor(null)}
                onConfirm={confirmarRetiro}
            />

            {/* Detalle */}
            <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Detalle</DialogTitle></DialogHeader>
                    {detalle && (
                        <div className="space-y-2 text-sm">
                            <Row label="Unidad" v={detalle.unidad_etiqueta} />
                            <Row label="Destinatario" v={detalle.destinatario_nombre || "—"} />
                            {detalle.destinatario_email && <Row label="Email" v={detalle.destinatario_email} />}
                            {detalle.destinatario_whatsapp && <Row label="WhatsApp" v={detalle.destinatario_whatsapp} />}
                            <Row label="Tipo" v={detalle.tipo} cap />
                            <Row label="Empresa" v={detalle.empresa_origen || "—"} />
                            <Row label="Llegada" v={fmtCuando(detalle.fecha_llegada || detalle.creado_en)} />
                            <Row label="Estado" v={detalle.estado === "pendiente" ? "Pendiente" : "Retirado"} />
                            {detalle.fecha_retiro && <Row label="Retiro" v={fmtCuando(detalle.fecha_retiro)} />}
                            {detalle.retirado_por_nombre && (
                                <Row
                                    label="Retiró"
                                    v={`${detalle.retirado_por_nombre}${detalle.retirado_por_tipo === "destinatario" ? " (destinatario)" : ""}${detalle.retirado_por_dni ? ` · DNI ${detalle.retirado_por_dni}` : ""}`}
                                />
                            )}
                            {detalle.observacion && <Row label="Observación" v={detalle.observacion} />}
                            <Row label="Cargó" v={detalle.creado_por_nombre || "—"} />
                            {detalle.foto_url && (
                                <div className="pt-2">
                                    <img src={detalle.foto_url} alt="paquete" className="rounded-md border border-border max-h-64 mx-auto" />
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="flex justify-between">
                        {detalle?.estado === "pendiente" ? (
                            <Button
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={async () => {
                                    if (!window.confirm("¿Eliminar este paquete?")) return;
                                    try { await api.delete(`/paqueteria/${detalle.paquete_id}`); setDetalle(null); await load(); }
                                    catch (e) { toast.error(formatApiError(e)); }
                                }}
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                            </Button>
                        ) : <span />}
                        <Button onClick={() => setDetalle(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DestinatarioPicker({ selected, onSelect, onManual }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (selected) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.get("/paqueteria/destinatarios", { params: { q, limit: 12 } });
                setResults(r.data || []);
            } finally { setLoading(false); }
        }, 200);
        return () => clearTimeout(debounceRef.current);
    }, [q, selected]);

    if (selected) {
        return (
            <div className="space-y-2">
                <Label className="text-xs">Destinatario</Label>
                <div className="flex items-start justify-between gap-2 rounded-md border border-border bg-primary-soft/30 px-3 py-2" data-testid="paq-form-destinatario-selected">
                    <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{selected.nombre}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                            <span className="font-mono">{selected.unidad_etiqueta}</span>
                            {selected.es_master && " · responsable"}
                            {!selected.activo && " · pendiente"}
                            {selected.email ? ` · ${selected.email}` : ""}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => onSelect(null)}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                        data-testid="paq-form-destinatario-clear"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
                    <span className="text-muted-foreground">Unidad: </span>
                    <span className="font-mono">{selected.unidad_etiqueta}</span>
                    <span className="text-muted-foreground"> · se completa automáticamente</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <Label className="text-xs">Destinatario</Label>
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    placeholder="Buscar por nombre, email, WhatsApp o unidad…"
                    className="pl-8"
                    data-testid="paq-form-destinatario-search"
                    autoFocus
                />
                {focused && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-md border border-border bg-popover shadow-lg">
                        {loading && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">Buscando…</div>
                        )}
                        {!loading && results.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados.</div>
                        )}
                        {!loading && results.map((r) => (
                            <button
                                key={r.usuario_edif_id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); onSelect(r); setQ(""); }}
                                className="w-full text-left px-3 py-2 hover:bg-primary-soft/40 border-b border-border last:border-0"
                                data-testid={`paq-form-destinatario-opt-${r.usuario_edif_id}`}
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-medium truncate">{r.nombre || "(Sin nombre)"}</span>
                                    <span className="text-[11px] font-mono text-primary shrink-0">{r.unidad_etiqueta}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                    {r.es_master ? "Responsable" : "Autorizado"}
                                    {!r.activo && " · pendiente"}
                                    {r.email ? ` · ${r.email}` : ""}
                                    {r.whatsapp ? ` · ${r.whatsapp}` : ""}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={onManual}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="paq-form-manual-toggle"
            >
                <UserPlus className="h-3 w-3" /> No está en la lista — cargar manualmente
            </button>
        </div>
    );
}

function ManualDestinatario({ form, unidades, onChange, onBack }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label className="text-xs">Destinatario (manual)</Label>
                <button
                    type="button"
                    onClick={onBack}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    data-testid="paq-form-manual-back"
                >
                    <ArrowLeft className="h-3 w-3" /> volver al buscador
                </button>
            </div>
            <Input
                value={form.destinatario_nombre_manual}
                onChange={(e) => onChange({ destinatario_nombre_manual: e.target.value })}
                placeholder="Nombre y apellido del destinatario"
                data-testid="paq-form-manual-nombre"
            />
            <Select value={form.unidad_id_manual} onValueChange={(v) => onChange({ unidad_id_manual: v })}>
                <SelectTrigger data-testid="paq-form-manual-unidad"><SelectValue placeholder="Elegí unidad" /></SelectTrigger>
                <SelectContent className="max-h-64">
                    {unidades.map((u) => (
                        <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.etiqueta}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
                No se crea ningún usuario. El paquete queda asociado a la unidad y el nombre indicado.
            </p>
        </div>
    );
}

function RetirarModal({ paquete, busy, onClose, onConfirm }) {
    const [quien, setQuien] = useState("destinatario");
    const [nombre, setNombre] = useState("");
    const [dni, setDni] = useState("");

    useEffect(() => {
        if (paquete) {
            setQuien("destinatario");
            setNombre("");
            setDni("");
        }
    }, [paquete]);

    if (!paquete) return null;

    const okDisabled = busy || (quien === "otra_persona" && !nombre.trim());

    return (
        <Dialog open={!!paquete} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Marcar como retirado</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="rounded-md bg-muted/40 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Paquete</p>
                        <p className="font-medium">{paquete.destinatario_nombre || paquete.unidad_etiqueta}</p>
                        <p className="text-[11px] text-muted-foreground">{paquete.unidad_etiqueta} · {paquete.tipo}{paquete.empresa_origen ? ` · ${paquete.empresa_origen}` : ""}</p>
                    </div>
                    <div>
                        <Label className="text-xs">¿Quién retira?</Label>
                        <RadioGroup value={quien} onValueChange={setQuien} className="mt-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <RadioGroupItem value="destinatario" id="ret-destinatario" data-testid="paq-retirar-destinatario" />
                                <span>Retira el destinatario {paquete.destinatario_nombre && <span className="text-muted-foreground">({paquete.destinatario_nombre})</span>}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <RadioGroupItem value="otra_persona" id="ret-otra" data-testid="paq-retirar-otra" />
                                <span>Retira otra persona</span>
                            </label>
                        </RadioGroup>
                    </div>
                    {quien === "otra_persona" && (
                        <div className="space-y-2 pl-1 border-l-2 border-primary-soft pl-3">
                            <div>
                                <Label className="text-xs">Nombre de quien retira *</Label>
                                <Input
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    placeholder="Nombre y apellido"
                                    data-testid="paq-retirar-nombre"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <Label className="text-xs">DNI (opcional)</Label>
                                <Input
                                    value={dni}
                                    onChange={(e) => setDni(e.target.value)}
                                    placeholder="Opcional"
                                    data-testid="paq-retirar-dni"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button
                        disabled={okDisabled}
                        onClick={() => onConfirm({
                            quien_retira: quien,
                            nombre_retira: quien === "otra_persona" ? nombre.trim() : null,
                            dni_retira: quien === "otra_persona" && dni.trim() ? dni.trim() : null,
                        })}
                        data-testid="paq-retirar-confirm"
                    >
                        <Check className="h-3.5 w-3.5 mr-1" /> Confirmar retiro
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, v, cap }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1 border-b border-border last:border-0">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className={`text-sm text-foreground text-right truncate ${cap ? "capitalize" : ""}`}>{v}</span>
        </div>
    );
}
